import { UserManager, UserManagerSettings, User, WebStorageStateStore } from 'oidc-client';
import config from "../../config";
import JwtDecode from 'jwt-decode';

class AuthService {
    private userManager: UserManager;
    private user: User | null;
    private userRoles: string[];
    constructor() {
        this.userManager = this.initializeUserManager();
        this.user = null;
        this.userRoles = [];

    }

    async init() {
        try {
            this.user = await this.userManager.getUser();
            this.userRoles = this.getUserRoles(this.user?.id_token);

            this.userManager.events.addUserLoaded((user: User) => {
                this.user = user;
                this.userRoles = this.getUserRoles(user?.id_token);
            });
        }
        catch (reason) {
            console.error(reason);
        };
    }

    private static authServiceInstance: AuthService;
    static async theInstance() {
        if (!this.authServiceInstance) {
            this.authServiceInstance = new AuthService();
            await this.authServiceInstance.init();
        }
        return this.authServiceInstance;
    }

    static theOtherInstance() {
        return this.authServiceInstance;
    }

    initializeUserManager(policy?: string, redirect_page?: string): UserManager {
        let settings: UserManagerSettings;
        // settings = this.configureB2C(policy, redirect_page);
        settings = this.configureOntimeIdentity(redirect_page);

        return new UserManager(settings);
    }
    configureOntimeIdentity(redirect_page: string | undefined): UserManagerSettings {
        const settings: UserManagerSettings = {
            metadataUrl: `${config.ontimeIdentity}/.well-known/openid-configuration`,
            authority: `${config.ontimeIdentity}/connect/authorize`,
            client_id: 'ontime-client',
            redirect_uri: redirect_page ? `${this.getBaseUrl()}/${redirect_page}` : `${this.getBaseUrl()}/signin-callback`,
            silent_redirect_uri: `${this.getBaseUrl()}/silent-renew.html`,
            automaticSilentRenew: true,
            post_logout_redirect_uri: `${this.getBaseUrl()}/signout-callback`,
            response_type: 'code',
            loadUserInfo: false,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            // scope: 'openid offline_access'
            scope: 'openid offline_access roles ontime_api'
        };
        return settings;
    }

    private configureB2C(policy: string | undefined, redirect_page: string | undefined) {
        if (!policy) {
            policy = 'B2C_1A_ot_signup_signin';
            // policy='B2C_1_OTSUSI'; 
        }
        const authority = `https://ontimebike.b2clogin.com/ontimebike.onmicrosoft.com/${policy}/oauth2/v2.0/`;
        const metadataUrl = `https://ontimebike.b2clogin.com/ontimebike.onmicrosoft.com/v2.0/.well-known/openid-configuration?p=${policy}`;
        const redirect_uri = redirect_page ? `${this.getBaseUrl()}/${redirect_page}` : `${this.getBaseUrl()}/signin-callback`;
        const settings: UserManagerSettings = {
            authority: authority,
            client_id: '6feb6d8b-8069-4e7c-bd3c-8d6756ea2c8b',
            redirect_uri: redirect_uri,
            silent_redirect_uri: `${this.getBaseUrl()}/silent-renew.html`,
            automaticSilentRenew: true,
            metadataUrl: metadataUrl,
            post_logout_redirect_uri: `${this.getBaseUrl()}/signout-callback`,
            response_type: 'code',
            loadUserInfo: false,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            scope: 'openid offline_access https://ontimebike.onmicrosoft.com/6feb6d8b-8069-4e7c-bd3c-8d6756ea2c8b/plus'
        };
        return settings;
    }

    private getBaseUrl(): string {
        return window.location.origin;
    }

    async login() {
        await this.userManager.signinRedirect();
    }

    async logout() {
        await this.userManager.signoutRedirect();
    }

    async signoutRedirectCallback() {
        await this.userManager.signoutRedirectCallback();
        this.user = null;
        this.userRoles = [];
    }

    async completeAuthentication(url?: string) {
        this.user = await this.userManager.signinRedirectCallback(url);
        this.userRoles = this.getUserRoles(this.user.id_token);
    }

    isLoggedIn(): boolean {
        if (this.user && this.user.id_token) {
            return !this.user.expired;
        }
        else {
            return false;
        }
    }

    getAccessToken(): string {
        return this.user ? this.user.access_token : '';
    }

    getIdToken(): string {
        return this.user ? this.user.id_token : '';
    }

    getUserRoles(token?: string): string[] {
        if (token) {
            let decodedToken: any = JwtDecode(token);
            if (decodedToken["role"] === undefined) {
                return [];
            }
            return decodedToken["role"];
        } else {
            return [];
        }
    }

    getUserRolesSmart(): string[] {
        if (this.userRoles === undefined || this.userRoles == null || this.userRoles.length === 0) {

            return this.userRoles;
        } else if (this.isLoggedIn()) {
            this.userRoles = this.getUserRoles(this.getIdToken())
        }
        return this.userRoles;
    }

    isInRole(...roleNames: string[]): boolean {
        let match = false;
        if (this.isLoggedIn() && roleNames && roleNames.length > 0) {
            roleNames.forEach(roleName => {
                if (this.getUserRolesSmart().indexOf(roleName) !== -1) {
                    match = true;
                }
            });
        }

        return match;
    }

    getUserId(): string | undefined {
        const token = this.getIdToken();
        if (token) {
            const decodedToken: any = JwtDecode(token);
            return decodedToken["sub"];
        }
        return undefined;
    }

    getIsOrganizer(organizerId: string): boolean {
        // if the user has the administrator role or it has the organizer role and its user id matches the event organizer id
        if (this.isInRole(config.managementGroups.administrator)) {
            return true;
        }
        if (this.isInRole(config.managementGroups.organizer)) {
            const userId = this.getUserId();
            return userId !== undefined && userId === organizerId;
        }
        return false;
    }

    async editProfile() {
        const editUserManager = this.initializeUserManager('B2C_1A_PROFILEEDIT', 'edit-callback.html');
        await editUserManager.signinRedirect();
    }
}

export { AuthService };